import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Save, Phone, Mail, MapPin, Building } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, getAuthHeader());
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#264653] font-['Nunito']">
            Einstellungen
          </h1>
          <p className="text-[#5F6F75] mt-2">
            Allgemeine Einstellungen und Kontaktdaten
          </p>
        </div>
        <Button 
          onClick={saveSettings}
          className="bg-[#F4A261] hover:bg-[#E76F51]"
          disabled={saving}
          data-testid="save-settings-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Wird gespeichert...' : 'Speichern'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Practice Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#264653] font-['Nunito']">
              <Building className="h-5 w-5" />
              Praxisinformationen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="practice_name">Praxisname</Label>
              <Input
                id="practice_name"
                value={settings.practice_name}
                onChange={(e) => setSettings({ ...settings, practice_name: e.target.value })}
                data-testid="settings-practice-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="therapist_name">Name der Therapeutin</Label>
              <Input
                id="therapist_name"
                value={settings.therapist_name}
                onChange={(e) => setSettings({ ...settings, therapist_name: e.target.value })}
                data-testid="settings-therapist-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="therapist_title">Titel</Label>
              <Input
                id="therapist_title"
                value={settings.therapist_title}
                onChange={(e) => setSettings({ ...settings, therapist_title: e.target.value })}
                placeholder="z.B. M.Sc.-Psych. Katharina Langer"
                data-testid="settings-therapist-title"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="therapist_subtitle">Untertitel</Label>
              <Input
                id="therapist_subtitle"
                value={settings.therapist_subtitle}
                onChange={(e) => setSettings({ ...settings, therapist_subtitle: e.target.value })}
                placeholder="z.B. Psychologische Psychotherapeutin"
                data-testid="settings-therapist-subtitle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#264653] font-['Nunito']">
              <Phone className="h-5 w-5" />
              Kontaktdaten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Telefonnummer</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="phone"
                  value={settings.phone}
                  onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                  className="pl-10"
                  data-testid="settings-phone"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">E-Mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                  className="pl-10"
                  data-testid="settings-email"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="terminservice_phone">Terminservicestelle</Label>
              <Input
                id="terminservice_phone"
                value={settings.terminservice_phone}
                onChange={(e) => setSettings({ ...settings, terminservice_phone: e.target.value })}
                placeholder="116-117"
                data-testid="settings-terminservice"
              />
            </div>
          </CardContent>
        </Card>

        {/* Addresses */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-[#264653] font-['Nunito']">
              <MapPin className="h-5 w-5" />
              Standorte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="address_borken">Adresse Borken</Label>
                <Input
                  id="address_borken"
                  value={settings.address_borken}
                  onChange={(e) => setSettings({ ...settings, address_borken: e.target.value })}
                  placeholder="z.B. Zentrum von Borken"
                  data-testid="settings-address-borken"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address_muenster">Adresse Münster</Label>
                <Input
                  id="address_muenster"
                  value={settings.address_muenster}
                  onChange={(e) => setSettings({ ...settings, address_muenster: e.target.value })}
                  placeholder="z.B. Gremmendorfer Weg 83, 48167 Münster"
                  data-testid="settings-address-muenster"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
